import type { Locale } from '@change-corgi/config/locales';
import { addQueryParam, retry } from '@change-corgi/core/http';
import type { Translations } from '@change-corgi/core/i18n';
import type { Utilities } from '@change-corgi/core/react/utilityContext';

import { getS3TranslationsUrl, getServerTranslationsUrl, getTranslationsOverrides } from 'src/shared/i18n';

const RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 250;

export type TranslationsOptions = {
	locale: Locale;
	cacheBuster?: string;
	/**
	 * Whether to use the front-end server to retrieve translations
	 *
	 * if false, translations will be retrieved from S3
	 */
	useServerTranslations: boolean;
};

export async function getTranslations(
	{ http, errorReporter }: Pick<Utilities, 'http' | 'errorReporter'>,
	{ locale, cacheBuster, useServerTranslations }: TranslationsOptions,
): Promise<Translations> {
	const overrides = getTranslationsOverrides(locale);
	if (overrides) {
		return Promise.resolve(overrides);
	}

	const i18nUrl = useServerTranslations
		? getServerTranslationsUrl(locale, cacheBuster)
		: getS3TranslationsUrl(locale, cacheBuster);

	// use getAsset to avoid sending custom headers, which seem to add 500ms of queuing to the request (at least in Chrome)
	return (
		retry({
			task: async () => http.getAsset<Translations>(i18nUrl),
			onRetry: async () => http.getAsset<Translations>(addQueryParam(i18nUrl, 'retrycb', String(Date.now()))),
			attempts: RETRY_ATTEMPTS,
			delay: RETRY_DELAY,
		})
			// eslint-disable-next-line promise/prefer-await-to-then
			.catch((response) => {
				void errorReporter.report({
					/* eslint-disable @typescript-eslint/no-unsafe-member-access */
					error: new Error(`Failed to retrieve i18n file after ${RETRY_ATTEMPTS} attempts with ${RETRY_DELAY}ms delay`),
					params: response.status
						? {
								message: response.statusText,
								status: response.status,
								url: i18nUrl,
								locale,
							}
						: {
								// eslint-disable-next-line @typescript-eslint/no-unsafe-call
								message: response.toString(),
								url: i18nUrl,
								locale,
							},
					/* eslint-enable @typescript-eslint/no-unsafe-member-access */
				});
				throw response;
			})
	);
}
